import {
  Checkbox,
  Grid,
  Group,
  Input,
  Radio,
  Select,
  useMantineColorScheme,
} from "@mantine/core";
import { IconPercentage, IconCaretDownFilled } from "@tabler/icons-react";
import AddAsset from "../Library/AddAsset.js";
import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur";
import { useAtom } from "jotai";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom.js";
import gensetJson from "../../components/help/helpCardMenu.json";
import { helpAtom } from "../../utils/atoms/helpAtom.js";
import { Trans, useTranslation } from "react-i18next";
import { helpContextMenuAtom } from "../../utils/atoms/helpContextMenuAtom.js";
import ProjectNumberInput from "../common/ProjectNumberInput";

const fuelTypes = [
  { value: "Diesel", label: "genset.fuel.diesel" },
  { value: "Biofuel", label: "genset.fuel.biofuel" },
  { value: "Natural Gas", label: "genset.fuel.naturalGas" },
];

const GensetConfigWrapper = ({
  batteryList,
  gensetParameters,
  handleSelectBatteryChange,
  handleInputChange,
  customButton,
  addAsset,
  form,
  popupStatus,
  addStatus,
  assetDetails,
  assetInfo,
  updatedAsset,
  onCloseAssetPopUp,
  onUpdateAsset,
  getAssetDetailsAttribute,
  addAttributeToLibrary,
  handleAsset,
  handleAssetSelection,
  asset,
  currentAvailableAttributes,
  isExtendedLibrary,
  getDefaultAssetsAttributeList,
  projectTypes,
  handleEditAsset,
  handleDeleteAsset,
  handleAssetAttribute,
  handleOkEditAsset,
  handleCancelUpdateAsset,
  setIsExtendedLibrary,
  inputRefs,
  handleFuelChange,
  setCurrentAvailableAttributes,
  setAssetDetails
}) => {
  const { t } = useTranslation();
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const [projectDetails] = useAtom(projectDetailsAtom);
  const [isGensetValue, setIsGensetValue] = useState(false);
  const getGensetValue = (val) => {
    val === 0 ? setIsGensetValue(true) : setIsGensetValue(false);
  };
  const getAssetIdValue = (val) => {
    const selectedGensetVal = val === 0 || val ? val : "";
    return selectedGensetVal;
  };

  const [helpMenuData] = useAtom(helpContextMenuAtom);
    
  const getFieldAttributes = (fieldKey) => {
    const field = helpMenuData?.genset?.[fieldKey];
    return {
      title: field?.title || "",
      "data-description": field?.description || "",
      // placeholder: field?.placeholder || "",
    };
  };
  const { colorScheme } = useMantineColorScheme();

  const grayIconStyle = useMemo(
    () => ({
      borderRadius: "4px",
      padding: "4px",
    }),
    [colorScheme],
  );

  // Reusable responsive column props (full width mobile, 2 cols small, 3 cols medium, 4 cols large)
  const responsiveCol = { span: 12, sm: 6, md: 4, lg: 3 };
  const isBaseloadGenset =
    (gensetParameters?.componentAttributes?.gensetParameters?.GensetType ||
      "ControlledGenset") === "BaseloadGenset";

  const degradationFactorValue = form?.values?.componentAttributes?.gensetParameters?.DegradationFactor;
  const lifetimeYearsValue = form?.values?.economicAttributes?.gensetParameters?.LifetimeYear;
  const degradationNote = useMemo(() => {
    if (
      degradationFactorValue === null ||
      degradationFactorValue === undefined ||
      String(degradationFactorValue).trim() === "" ||
      lifetimeYearsValue === null ||
      lifetimeYearsValue === undefined ||
      String(lifetimeYearsValue).trim() === ""
    ) {
      return null;
    }

    const yearlyPercentage = Number(degradationFactorValue);
    const lifetime = Number(lifetimeYearsValue);
    if (!Number.isFinite(yearlyPercentage) || !Number.isFinite(lifetime) || lifetime <= 0) {
      return null;
    }

    const averageReduction = (yearlyPercentage / 100) * ((lifetime + 1) / 2) * 100;
    const reductionText = Number.isInteger(averageReduction)
      ? String(averageReduction)
      : averageReduction.toFixed(1).replace(/\.0$/, "");

    return {
      dfText: yearlyPercentage.toFixed(1),
      reductionText,
    };
  }, [degradationFactorValue, lifetimeYearsValue]);

  const containerRef = useRef(null);
  useHelpFocusBlur(containerRef, setHelperAtom, helperAtom);

  const handleBlur = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setHelperAtom({
        ...helperAtom,
        isFocused: false,
        helpSection: {
          title: "",
          description: "",
          type: "",
          other: "",
        },
      });
    }
  };
  const handleFocus = (event) => {
    const dataId = event.target.getAttribute("data-id");
    if (dataId) {
      const cardElement = document.querySelector(`[card-id="${dataId}"]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
        cardElement.style.transition = "background-color 0.5s ease";
        cardElement.style.border = "2px solid #42b4e6";
        setTimeout(() => {
          cardElement.style.border = "";
        }, 2000);
      }
    }
  };

  useEffect(() => {
    const isAssetIdPresent =
      gensetParameters?.uiAttributes?.gensetParameters?.assetId;
    if (isAssetIdPresent) {
      getGensetValue(isAssetIdPresent);
    }
  }, [gensetParameters?.uiAttributes?.gensetParameters?.assetId]);

  // Only run help logic if the event target is the container itself
  const handleContainerClick = (event) => {
    const interactiveTags = ["INPUT", "SELECT", "TEXTAREA", "BUTTON", "LABEL"];
    if (interactiveTags.includes(event.target.tagName)) return;
    const cardElement = document.querySelector('[card-id="accordion-genset-1"]');
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
      cardElement.style.transition = "background-color 0.5s ease";
      cardElement.style.border = "2px solid #42b4e6";
      setTimeout(() => {
        cardElement.style.border = "";
      }, 2000);
    }
  };

  const handleContainerKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContainerClick(event);
    }
  };

  return (
    <div
      ref={containerRef}
      data-id="accordion-genset-1"
      onClick={handleContainerClick}
      onKeyDown={handleContainerKeyDown}
      tabIndex={0}
      role="button"
    >
      <h5>{t("genset.selectIntro")}</h5>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <Select
                {...getFieldAttributes("field-1")}
                data-id="field-1"
                label={t("genset.selectGenset.label")}
                rightSection={<IconCaretDownFilled size={18} />}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                placeholder={t("genset.selectGenset.placeholder")}
                data={batteryList}
                value={getAssetIdValue(
                  gensetParameters?.uiAttributes?.gensetParameters?.assetId
                )}
                {...form.getInputProps("uiAttributes.gensetParameters.assetId")}
                onChange={(value) => {
                  const selectedGensetValue = batteryList?.find(
                    (option) => option?.value === value
                  );
                  handleSelectBatteryChange(value);
                  getGensetValue(value);
                  handleInputChange("uiAttributes", "assetId", value);
                  handleInputChange(
                    "uiAttributes",
                    "selectGenset",
                    selectedGensetValue?.label
                  );
                }}
                required
                withinPortal
                searchable
                onClick={e => e.stopPropagation()}
              />
            </div>
            {gensetParameters?.uiAttributes?.gensetParameters?.assetId === 0 && (
              <se-button
                onClick={addAsset}
                style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
              >
                {t("genset.addCustom")}
              </se-button>
            )}
          </div>
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <Select
            data-id="field-21"
            {...getFieldAttributes("field-21")}
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("genset.gensetType.label")}
            data={[
              { value: 'ControlledGenset', label: t("genset.gensetType.controlled") },
              { value: 'BaseloadGenset', label: t("genset.gensetType.baseload") },
            ]}
            value={
              gensetParameters?.componentAttributes?.gensetParameters
                ?.GensetType || "ControlledGenset"
            }
            {...form.getInputProps(
              "componentAttributes.gensetParameters.GensetType"
            )}
            onChange={(value) => {
              handleInputChange("componentAttributes", "GensetType", value);
              if (value === "BaseloadGenset") {
                handleFuelChange("Natural Gas");
                handleInputChange("uiAttributes", "gensetEconomicOptimization", false);
              } else if (value === "ControlledGenset") {
                handleFuelChange("Diesel");
                handleInputChange("uiAttributes", "gensetEconomicOptimization", true);
              }
            }}
            withinPortal
            required
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <Select
            data-id="field-gen"
            {...getFieldAttributes("field-gen")}
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("genset.selectNumber.label")}
            // placeholder={t("genset.selectNumber.placeholder")}
            data={[
              { value: 1, label: 1 },
              { value: 2, label: 2 },
              { value: 3, label: 3 },
              { value: 4, label: 4 },
              { value: 5, label: 5 },
            ]}
            value={
              gensetParameters?.componentAttributes?.gensetParameters
                ?.NbGenset || 1
            }
            {...form.getInputProps(
              "componentAttributes.gensetParameters.NbGenset"
            )}
            onChange={(value) => {
              handleInputChange("componentAttributes", "NbGenset", value);
            }}
            withinPortal
            required
          />
        </Grid.Col>
      </Grid>
      <h5>{t("genset.configurationParameters")}</h5>
    {!isBaseloadGenset && (
    <Grid gutter="xs" className="mt-2 mb-2">
        &nbsp;&nbsp;<Checkbox
          data-id="field-cost"
          {...getFieldAttributes("field-cost")}
          onClick={e => e.stopPropagation()}
          labelPosition="left"
          label={t("genset.costOptimization.label")}
          checked={
            gensetParameters?.uiAttributes?.gensetParameters
              ?.gensetEconomicOptimization || false
          }
          {...form.getInputProps(
            "uiAttributes.gensetParameters.gensetEconomicOptimization"
          )}
          onChange={(event) => {
            const value = event.target.checked;
            handleInputChange(
              "uiAttributes",
              "gensetEconomicOptimization",
              value
            );
          }}
        />
    </Grid>
    )}
      {/* Combined primary genset configuration fields into a single grid for 4/4/(remaining) responsive layout */}
      <Grid gutter="md" className="mt-2 mb-2">
        {/* 1 */}
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            onClick={e => e.stopPropagation()}
            {...getFieldAttributes("field-2")}
            data-id="field-2"
            label={t("genset.ratedPower.label")}
            required
            placeholder={t("genset.ratedPower.placeholder")}
            {...form.getInputProps(
              "componentAttributes.gensetParameters.PowElecNom"
            )}
            onChange={(event) => {
              handleInputChange("componentAttributes", "PowElecNom", event.target.value);
            }}
            rightSection={<div style={grayIconStyle}>kW</div>}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        {gensetParameters?.componentAttributes?.gensetParameters
          ?.GensetType==="BaseloadGenset"&&
          <>
          <Grid.Col {...responsiveCol}>
            <ProjectNumberInput
              onClick={e => e.stopPropagation()}
              {...getFieldAttributes("field-4")}
              data-id="field-4"
              label={t("genset.heatRecoveryRatio.label")}
              required
              {...form.getInputProps(
                "componentAttributes.gensetParameters.HeatRecoveryRatio"
              )}
              onChange={(e) => {
                handleInputChange(
                  "componentAttributes",
                  "HeatRecoveryRatio",
                  e.currentTarget.value
                );
              }}
              rightSection={
                <div style={{ ...grayIconStyle }}>
                  <IconPercentage size={18} />
                </div>
              }
            />
        </Grid.Col>
        </>
        }
        {/* 2 */}
        {gensetParameters?.componentAttributes?.gensetParameters
          ?.GensetType==="ControlledGenset"&&
          <>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            onClick={e => e.stopPropagation()}
            {...getFieldAttributes("field-3")}
            data-id="field-3"
            required
            {...form.getInputProps(
              "componentAttributes.gensetParameters.MinPowerRate"
            )}
            onChange={(event) => {
              handleInputChange(
                "componentAttributes",
                "MinPowerRate",
                event.target.value
              );
            }}
            label={t("genset.minPower.label")}
            placeholder={t("genset.minPower.placeholder")}
            rightSection={
              <div style={{ ...grayIconStyle }}>
                <IconPercentage size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        {/* 3 */}
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            onClick={e => e.stopPropagation()}
            {...getFieldAttributes("field-5")}
            data-id="field-5"
            label={t("genset.minRunTime.label")}
            required
            {...form.getInputProps(
              "componentAttributes.gensetParameters.MinimumRunTime"
            )}
            onChange={(e) => {
              handleInputChange(
                "componentAttributes",
                "MinimumRunTime",
                e.currentTarget.value
              );
            }}
            placeholder={t("genset.minRunTime.placeholder")}
            rightSection={
              <div style={{ ...grayIconStyle, marginRight: "2rem" }}>
                seconds
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
            <ProjectNumberInput
              onClick={e => e.stopPropagation()}
              {...getFieldAttributes("field-8")}
              data-id="field-8"
              label={t("genset.powerReserve.label")}
              required
              placeholder={t("genset.powerReserve.placeholder")}
              {...form.getInputProps(
                "componentAttributes.gensetParameters.PowerReserve"
              )}
              onChange={(e) => {
                handleInputChange(
                  "componentAttributes",
                  "PowerReserve",
                  e.currentTarget.value.trim()
                );
              }}
              rightSection={
                <div style={{ ...grayIconStyle }}>
                  <IconPercentage size={18} />
                </div>
              }
              rightSectionProps={{ style: { pointerEvents: "none" } }}
            />
        </Grid.Col>
        </>
        }
        {/* 5 */}
        {/* 6 */}
        <Grid.Col {...responsiveCol}>
          <Input.Wrapper withAsterisk>
            <ProjectNumberInput
              onClick={e => e.stopPropagation()}
              {...getFieldAttributes("field-7")}
              data-id="field-7"
              label={t("genset.degradation.label")}
              required
              {...form.getInputProps(
                "componentAttributes.gensetParameters.DegradationFactor"
              )}
              onChange={(e) => {
                handleInputChange(
                  "componentAttributes",
                  "DegradationFactor",
                  e.currentTarget.value.trim()
                );
              }}
              placeholder={t("genset.degradation.placeholder")}
              rightSection={
                <div style={{ ...grayIconStyle }}>
                  <IconPercentage size={18} />
                </div>
              }
              rightSectionProps={{ style: { pointerEvents: "none" } }}
            />
            {degradationNote && (
              <div style={{ marginTop: "6px", color: "#eb7857", fontSize: "12px", lineHeight: 1.35 }}>
                <Trans
                  i18nKey="component.degradation.note"
                  values={{ df: degradationNote.dfText, reduction: degradationNote.reductionText }}
                  components={{ strong: <strong /> }}
                />
              </div>
            )}
          </Input.Wrapper>
        </Grid.Col>
        {/* 7 (conditional) */}
        {/* {gensetParameters?.componentAttributes?.gensetParameters?.NbGenset !== 1 && ( */}

        {/* )} */}
                <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            onClick={e => e.stopPropagation()}
            data-id="field-life"
            {...getFieldAttributes("field-life")}
            label={t("genset.operatingHours.label")}
            required
            placeholder={t("genset.operatingHours.placeholder")}
            {...form.getInputProps(
              "economicAttributes.gensetParameters.LifetimeHour"
            )}
            onChange={(e) => {
              handleInputChange(
                "economicAttributes",
                "LifetimeHour",
                e.currentTarget.value.trim()
              );
            }}
            rightSection={
              <div style={{ ...grayIconStyle, marginRight: "42%" }}>Hours</div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
      </Grid>
      <h5>{t("genset.fuelConfig")}</h5>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-9")}
            data-id="field-9"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("genset.selectFuel.label")}
            placeholder={t("genset.selectFuel.placeholder")}
            data={fuelTypes.map((fuel) => ({ ...fuel, label: t(fuel.label) }))}
            {...form.getInputProps(
              "componentAttributes.gensetParameters.Fuel.Name"
            )}
            onChange={(value) => {
              handleFuelChange(value);
            }}
            required
            withinPortal
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            {...getFieldAttributes("field-13")}
            data-id="field-13"
            label={t("genset.pricePerUnit.label")}
            {...form.getInputProps(
              "componentAttributes.gensetParameters.Fuel.PricePerM3"
            )}
            onChange={(event) => {
              handleInputChange("componentAttributes", "Fuel.PricePerM3", event.currentTarget.value);
            }}
            required
            placeholder={t("genset.pricePerUnit.placeholder")}
            rightSection={
              <div style={{ ...grayIconStyle, marginRight: "0.6rem" }}>
                {projectDetails?.projectDTO?.currencyCode || "$"}/m3
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            rightSectionWidth={60}
          />
        </Grid.Col>
      </Grid>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            {...getFieldAttributes("field-10")}
            data-id="field-10"
            label={t("genset.lhv.label")}
            required
            {...form.getInputProps(
              "componentAttributes.gensetParameters.Fuel.LHV"
            )}
            onChange={(event) => {
              handleInputChange("componentAttributes", "Fuel.LHV", event.target.value);
            }}
            placeholder={t("genset.lhv.placeholder")}
            rightSection={
              <div style={{ ...grayIconStyle, marginRight: "1rem" }}>MJ/kg</div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            {...getFieldAttributes("field-11")}
            data-id="field-11"
            required
            label={t("genset.density.label")}
            {...form.getInputProps(
              "componentAttributes.gensetParameters.Fuel.Density"
            )}
            onChange={(event) => {
              handleInputChange(
                "componentAttributes",
                "Fuel.Density",
                event.target.value
              );
            }}
            placeholder={t("genset.density.placeholder")}
            rightSection={
              <div style={{ ...grayIconStyle, marginRight: "1.3rem" }}>
                kg/m3
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>

        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            {...getFieldAttributes("field-12")}
            data-id="field-12"
            label={t("genset.carbonContent.label")}
            required
            {...form.getInputProps(
              "componentAttributes.gensetParameters.Fuel.Carbon"
            )}
            onChange={(e) => {
              handleInputChange(
                "componentAttributes",
                "Fuel.Carbon",
                e.currentTarget.value.trim()
              );
            }}
            placeholder={t("genset.carbonContent.placeholder")}
            rightSection={
              <div style={{ ...grayIconStyle }}>
                <IconPercentage size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
      </Grid>
      <Grid gutter="md" className="mt-2 mb-2">
        
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            {...getFieldAttributes("field-14")}
            data-id="field-14"
            required
            label={t("genset.slopeCoefficient.label")}
            {...form.getInputProps(
              "componentAttributes.gensetParameters.FuelCurveSlope"
            )}
            onChange={(event) => {
              handleInputChange(
                "componentAttributes",
                "FuelCurveSlope",
                event.target.value
              );
            }}
            placeholder={t("genset.slopeCoefficient.placeholder")}
            rightSection={
              <div style={{ ...grayIconStyle, marginRight: "4rem" }}>
                m3/kW/Hour
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            {...getFieldAttributes("field-15")}
            data-id="field-15"
            label={t("genset.interceptCoefficient.label")}
            placeholder={t("genset.interceptCoefficient.placeholder")}
            {...form.getInputProps(
              "componentAttributes.gensetParameters.FuelCurveIntercept"
            )}
            required
            onChange={(event) => {
              handleInputChange(
                "componentAttributes",
                "FuelCurveIntercept",
                event.target.value
              );
            }}
            rightSection={
              <div style={{ ...grayIconStyle, marginRight: "2.2rem" }}>
                m3/Hour
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
      </Grid>
      {/* <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col span={12}>
          <Radio.Group
            onClick={e => e.stopPropagation()}
            {...getFieldAttributes("field-16")}
            data-id="field-16"
            name="favoriteFramework"
            label={t("genset.powerProfile.label")}
            withAsterisk
            value={"Power"}
            {...form.getInputProps(
              "componentAttributes.gensetParameters.PowerProfile"
            )}
            onChange={(event) => {
              const value = event;
              handleInputChange("componentAttributes", "PowerProfile", value);
            }}
          >
            <Group mt="xs">
              <Radio
                label={t("genset.powerProfile.power")}
                value="Power"
              />
              <Radio
                disabled
                label={t("genset.powerProfile.electrical")}
                value="ProfElec"
              />
              <Radio
                disabled
                label={t("genset.powerProfile.thermal")}
                value="ProfTherm"
              />
            </Group>
          </Radio.Group>
        </Grid.Col>
      </Grid> */}

      {popupStatus.addAssetPopupStatus && (
        createPortal(
          <AddAsset
            getDefaultAssetsAttributeList={getDefaultAssetsAttributeList}
            addAssetPopupStatus={popupStatus.addAssetPopupStatus}
            addStatus={addStatus}
            assetsType={assetDetails?.assetsType}
            assetAttributes={assetDetails?.assetAttributes}
            assetDetailsData={assetDetails}
            manufacturerList={assetInfo.manufacturerList}
            updatedAsset={updatedAsset}
            onCloseAssetPopUp={onCloseAssetPopUp}
            onUpdateAsset={onUpdateAsset}
            getAssetDetailsAttribute={getAssetDetailsAttribute}
            addAttributeToLibrary={addAttributeToLibrary}
            handleAsset={handleAsset}
            handleAssetSelection={handleAssetSelection}
            asset={asset}
            projectTypes={projectTypes}
            handleEditAsset={handleEditAsset}
            handleDeleteAsset={handleDeleteAsset}
            handleAssetAttribute={handleAssetAttribute}
            handleOkEditAsset={handleOkEditAsset}
            handleCancelUpdateAsset={handleCancelUpdateAsset}
            setIsExtendedLibrary={setIsExtendedLibrary}
            isExtendedLibrary={isExtendedLibrary}
            inputRefs={inputRefs}
            currentAvailableAttributes={currentAvailableAttributes}
            setCurrentAvailableAttributes={setCurrentAvailableAttributes}
            isAssetTypeDisabled={true}
            isPortaled={true}
            setAssetDetailsEx={setAssetDetails}
          />,
          document.body
        )
      )}
    </div>
  );
};

export default GensetConfigWrapper;
